import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { BooleanInput, DateTimeInput, TextInput, required } from "react-admin";
import { useWatch } from "react-hook-form";

export function transformRelayEventPayload(data: Record<string, unknown>) {
  const next = { ...data };

  if (next.isDefault) {
    next.requiresAccessGrant = false;
    next.otpRequired = false;
  }

  return next;
}

export function validateRelayEventForm(values: Record<string, unknown>) {
  const errors: Record<string, string> = {};

  if (values.isDefault && values.requiresAccessGrant) {
    errors.requiresAccessGrant = "An event cannot be both default and access-gated";
  }

  return errors;
}

export default function RelayEventFormFields() {
  const isDefault = useWatch({ name: "isDefault" });
  const requiresAccessGrant = useWatch({ name: "requiresAccessGrant" });

  return (
    <>
      <TextInput source="name" label="Internal name" fullWidth validate={required()} />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Event classification
      </Typography>

      <BooleanInput
        source="isDefault"
        label="Default event for all users"
        helperText="Only one default event. All ITS users get access without a grant or OTP."
      />
      <BooleanInput
        source="requiresAccessGrant"
        label="Requires access grant"
        disabled={Boolean(isDefault)}
        helperText="Staff must grant access per ITS user. Mutually exclusive with default."
      />
      {isDefault && requiresAccessGrant ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          An event cannot be both default and access-gated.
        </Alert>
      ) : null}

      <BooleanInput source="isActive" label="Active" defaultValue />
      <BooleanInput
        source="otpRequired"
        label="Require OTP before streaming"
        disabled={Boolean(isDefault)}
        helperText={
          isDefault
            ? "Default events never require OTP."
            : "When enabled, the relay app sends phone OTP before playback."
        }
      />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Stream content
      </Typography>

      <TextInput source="title" label="Display title" fullWidth />
      <TextInput source="description" label="Description" fullWidth multiline minRows={3} />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
          <DateTimeInput
            source="eventTime"
            label="Event start"
            fullWidth
            helperText="For display only — does not control when the stream goes live"
          />
        </Box>
        <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
          <DateTimeInput
            source="eventEndTime"
            label="Event end"
            fullWidth
            helperText="For display only"
          />
        </Box>
      </Box>
      <TextInput
        source="youtubeVideoId"
        label="YouTube video ID"
        fullWidth
        helperText="Video ID only, not the full URL"
      />
      <TextInput source="pdfDownloadUrl" label="PDF download URL" fullWidth />
      <BooleanInput source="isLive" label="Live now" />
      <BooleanInput
        source="castEnabled"
        label="Enable Chromecast (Android TV)"
        helperText="When enabled, viewers can play this stream on a Chromecast or Android TV from the relay app."
      />
      <BooleanInput
        source="singleSessionEnabled"
        label="Single device session"
        helperText="When enabled, only one phone/browser may watch this relay stream at a time. Chromecast can still run alongside the phone."
      />
      <BooleanInput
        source="bannerEnabled"
        label="Show Ya Ali / Ya Hussain banner"
        helperText="When enabled, the relay app shows the commemorative banner below the app bar on this event."
      />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Access approval messaging
      </Typography>

      <TextInput
        source="whatsappGroupLink"
        label="WhatsApp group link"
        fullWidth
        helperText="Invite link included when staff copy the relay access approval message (chat.whatsapp.com/…)"
      />
    </>
  );
}
